import React, { useState, useEffect } from 'react';
import { useDashboardStats } from '@/pages/Home/api/useDashboardStats';
import { usePutHomePage, HomePageConfig } from './api/usePutHomePage';

const TITLE_LIMIT = 80;
const BODY_LIMIT = 300;

function CharCount({ value, limit }: { value: string; limit: number }) {
  const remaining = limit - value.length;
  const near = remaining <= limit * 0.15;
  const over = remaining < 0;
  return (
    <span className={`text-xs tabular-nums ${over ? 'text-red-400' : near ? 'text-amber-400' : 'text-gray-500'}`}>
      {value.length} / {limit}
    </span>
  );
}

export function HomePageTab(): React.ReactElement {
  const { data, loading: fetching } = useDashboardStats();
  const { save, saving, error } = usePutHomePage();
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState<HomePageConfig>({
    employee_count: 0,
    active_markets: 0,
    rollout_status: '',
    announcement_title: '',
    announcement_body: '',
  });

  // Pre-populate form once data loads
  useEffect(() => {
    if (!data) return;
    setForm({
      employee_count: data.employee_count ?? 0,
      active_markets: data.metrics?.active_markets ?? 0,
      rollout_status: data.metrics?.rollout_status ?? '',
      announcement_title: data.announcement?.title ?? '',
      announcement_body: data.announcement?.body ?? '',
    });
  }, [data]);

  const handleChange = (field: keyof HomePageConfig, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const titleOver = form.announcement_title.length > TITLE_LIMIT;
  const bodyOver = form.announcement_body.length > BODY_LIMIT;
  const hasLimitError = titleOver || bodyOver;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hasLimitError) return;
    const ok = await save({
      ...form,
      employee_count: Number(form.employee_count),
      active_markets: Number(form.active_markets),
    });
    if (ok) setSaved(true);
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-gray-400 text-sm">Loading current config…</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white">Home Page Config</h2>
        <p className="text-gray-400 text-sm mt-1">
          These values appear on the home page hero stats and announcement card.
          Changes take effect immediately.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">

        {/* Stats */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-5">
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Hero Stats</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Team Members</label>
              <input
                type="number"
                min={0}
                value={form.employee_count}
                onChange={e => handleChange('employee_count', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Active Markets</label>
              <input
                type="number"
                min={0}
                value={form.active_markets}
                onChange={e => handleChange('active_markets', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Rollout Status</label>
            <input
              type="text"
              value={form.rollout_status}
              onChange={e => handleChange('rollout_status', e.target.value)}
              placeholder="e.g. Live, Beta, Coming Soon"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
            />
          </div>
        </div>

        {/* Announcement */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Announcement</h3>
            <p className="text-gray-500 text-xs mt-1">Leave both fields empty to hide the announcement card.</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-300">Title</label>
              <CharCount value={form.announcement_title} limit={TITLE_LIMIT} />
            </div>
            <input
              type="text"
              value={form.announcement_title}
              onChange={e => handleChange('announcement_title', e.target.value)}
              placeholder="Short headline for the announcement"
              className={`w-full bg-gray-800 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 ${
                titleOver
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-700 focus:border-violet-500 focus:ring-violet-500'
              }`}
            />
            {titleOver && (
              <p className="text-red-400 text-xs mt-1">Title must be {TITLE_LIMIT} characters or fewer.</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-300">Body</label>
              <CharCount value={form.announcement_body} limit={BODY_LIMIT} />
            </div>
            <textarea
              value={form.announcement_body}
              onChange={e => handleChange('announcement_body', e.target.value)}
              rows={6}
              placeholder="Announcement details — keep it concise so it fits cleanly in the hero card."
              className={`w-full bg-gray-800 border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-1 resize-none ${
                bodyOver
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-700 focus:border-violet-500 focus:ring-violet-500'
              }`}
            />
            {bodyOver && (
              <p className="text-red-400 text-xs mt-1">Body must be {BODY_LIMIT} characters or fewer.</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={saving || hasLimitError}
            className="px-6 py-2.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>

          {saved && !error && (
            <span className="text-sm text-emerald-400 flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Saved
            </span>
          )}

          {error && (
            <span className="text-sm text-red-400">{error}</span>
          )}
        </div>
      </form>
    </div>
  );
}
